import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message, Button, Alert } from 'antd';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject, Filter, Toolbar, Edit, CommandColumn } from '@syncfusion/ej2-react-grids';
import { DropDownList } from '@syncfusion/ej2-dropdowns';
import { createUser, fetchUsers, updateUser, reinviteUser } from '../../redux/slices/usersSlice';
import styles from './Offices.module.css';
import { useTranslation } from 'react-i18next';
import { translateBackendMessage } from '../../utils/translateMessage';


const Offices = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const gridRef = useRef(null);
  const containerRef = useRef(null);
  // Ref + DOM class (not state) so toggling add mode doesn't re-render the grid mid-edit
  const isAddingRef = useRef(false);
  const setIsAdding = (value) => {
    isAddingRef.current = value;
    containerRef.current?.classList.toggle(styles.adding, value);
  };
  const {
    loading,
    usersList,
    error
  } = useSelector(state => state.users);

  const roleOptions = ['OFFICE', 'STAFF'];
  const statusOptions = ['Invited', 'Disabled', 'Suspended', 'Active'];
  const commands = [{ type: 'Reinvite', buttonOption: { iconCss: 'e-icons e-send', cssClass: 'e-flat' } } ];

  const statusLookup = statusOptions.map((value) => ({
    value,
    text: t(`status.${String(value).toLowerCase()}`, { defaultValue: value }),
  }));
  const getStatusText = (value) =>
    t(`status.${String(value).toLowerCase()}`, { defaultValue: value });

  const getStatusValueFromText = (text) => {
    const found = statusLookup.find((x) => x.text === text);
    return found?.value ?? text;
  };
  
  const filterSettings = { type: 'Excel' };
  const toolbarOptions = ['Add', 'Edit', 'Update', 'Cancel', 'Search'];
  const pageSettings = { pageCount: 5 };
  const editSettings = {
    allowEditing: true,
    allowAdding: true, // UI only (we cancel on save because backend has no create endpoint)
    allowDeleting: false,
    // mode: 'Normal',
  };

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const rawMessage = typeof error === 'string' ? error : (error?.message || error?.error);
      const translated = translateBackendMessage(rawMessage);
      const fallback = t('notifications.frontend.offices.failedToLoadUsers');
      message.error(translated || rawMessage || fallback);
    }
  }, [error, t]);

  async function actionBegin(args) {
    // Track when the add row opens/closes ('add' = new row, 'beginEdit' = existing row)
    if (args.requestType === 'add') {
      setIsAdding(true);
      return;
    }
    if (args.requestType === 'beginEdit') {
      setIsAdding(false);
      return;
    }

    // On save, add mode is cleared only after a successful create (the row stays open on failure)
    if (args.requestType === 'cancel') {
      setIsAdding(false);
    }

    if (args.requestType !== 'save') return;

    // Validate required fields for add operation
    if (args.action === 'add') {
      const requiredFields = ['userId', 'username', 'email'];
      const missingFields = requiredFields.filter(field => !args.data[field] || args.data[field].trim() === '');
      
      if (missingFields.length > 0) {
        message.error(t('notifications.frontend.offices.requiredFieldsMissing', {
          missingFields: missingFields.join(', '),
        }));
        args.cancel = true;
        return;
      }
    }

    // prevent Grid from inserting/updating locally
    args.cancel = true;

    try {
      if (args.action === 'add') {
        const createPayload = { ...(args.data || {}) };
        createPayload.status =
          getStatusValueFromText(createPayload.statusText) || statusOptions[0];
        delete createPayload.statusText;

        await dispatch(createUser(createPayload)).unwrap();
        gridRef.current?.closeEdit();
        setIsAdding(false);
        return;
      }

      if (args.action === 'edit') {
        const previousData = args.previousData || {};
        const currentData = args.data || {};
        const currentStatusValue =
          getStatusValueFromText(currentData.statusText) || currentData.status || statusOptions[0];
        
        // Build update payload with only changed fields
        const updatePayload = {
          userId: currentData.userId, // Always include userId for identification
        };
        
        // Compare each field and only include changed ones
        const fieldsToCheck = ['role', 'username', 'email', 'mobile', 'officeOwner'];
        fieldsToCheck.forEach((field) => {
          if (previousData[field] !== currentData[field]) {
            updatePayload[field] = currentData[field];
          }
        });

        // Status is edited/displayed via Hebrew text field, but backend expects English enum.
        if (previousData.status !== currentStatusValue) {
          updatePayload.status = currentStatusValue;
        }
        
        await dispatch(updateUser(updatePayload)).unwrap();

        // message.success('User updated successfully');
        gridRef.current?.closeEdit();
        setIsAdding(false);
      }
    } catch (e) {
      const msg =
        typeof e === 'string'
          ? e
          : (e?.message || e?.error || t('notifications.frontend.offices.updateUserFailed'));
      const translated = translateBackendMessage(msg);
      // message.error(translated || msg);
    }
  }

  const handleReinvite = async (rowData) => {
    try {
      const userId = rowData?.userId || rowData?.id;
      // A row that is still being added has no saved userId yet
      if (isAddingRef.current || !userId) {
        message.warning(t('notifications.frontend.offices.saveBeforeReinvite'), 8);
        return;
      }
      await dispatch(reinviteUser(userId)).unwrap();
      message.success(t('notifications.frontend.offices.reinviteSuccess'));
    } catch (e) {
      const msg =
        typeof e === 'string'
          ? e
          : (e?.message || e?.error || t('notifications.frontend.offices.reinviteFailed'));
      const translated = translateBackendMessage(msg);
      message.error(translated || msg);
    }
  };

  const commandClick = (args) => {
    if (args.commandColumn.type === 'Reinvite') {
      handleReinvite(args.rowData);
    }
  };

  // Template for role field - disabled during add
  const RoleEditTemplate = (props) => {
    const handleChange = (e) => {
      if (props.role !== undefined) {
        props.role = e.target.value;
      }
    };
    
    return (
      <select
        className="e-input"
        value={props.role || ''}
        disabled={isAddingRef.current}
        onChange={handleChange}
      >
        {roleOptions.map(option => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    );
  };

  // Status editor: show Hebrew, but save English enum value (so backend validation passes)
  const statusEdit = (() => {
    let dropdownObj;
    const dataSource = statusLookup;

    return {
      create: () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'text');
        return input;
      },
      write: (args) => {
        const currentValue =
          args?.rowData?.status ||
          getStatusValueFromText(args?.rowData?.statusText) ||
          statusOptions[0];
        dropdownObj = new DropDownList({
          dataSource,
          fields: { text: 'text', value: 'value' },
          value: currentValue,
          enableRtl: true,
          allowFiltering: true,
          popupHeight: '220px',
        });
        dropdownObj.appendTo(args.element);
      },
      // Store Hebrew text in the grid field (so Excel filter shows Hebrew distinct values)
      read: () => dropdownObj?.text,
      destroy: () => {
        if (dropdownObj) {
          dropdownObj.destroy();
          dropdownObj = null;
        }
      },
    };
  })();

  return (
    <div className="control-pane">
      <div className="control-section">
        <h1>{t('offices.title')}</h1>
        <div ref={containerRef} className={styles.container}>
          <Alert type="info" showIcon message={t('offices.addHint')} className={styles.addHint} />

          <GridComponent
            id="officesGrid"
            ref={gridRef}
            dataSource={(usersList || []).map((u) => ({
              ...u,
              statusText: getStatusText(u?.status || ''),
            }))}
            locale={i18n?.language || 'he'}
            toolbar={toolbarOptions}
            allowSorting={true}
            allowFiltering={true}
            filterSettings={filterSettings}
            allowPaging={true}
            pageSettings={pageSettings}
            enableRtl={true}
            loadingIndicator={{ indicatorType: 'Shimmer' }}
            editSettings={editSettings}
            actionBegin={actionBegin}
            commandClick={commandClick}
          >
            <ColumnsDirective>
              {/* <ColumnDirective field="id" visible={false} isPrimaryKey={true} /> */}
              <ColumnDirective field="userId" headerText={t('offices.User ID')} width="140" textAlign="Center" validationRules={{ required: true }} />
              <ColumnDirective field="username" headerText={t('offices.Username')} width="180" textAlign="Center" validationRules={{ required: true }} />
              <ColumnDirective field="email" headerText={t('offices.Email')} width="220" textAlign="Center" validationRules={{ required: true, email: true }} />
              <ColumnDirective field="mobile" headerText={t('offices.Mobile')} width="140" textAlign="Center" />
              <ColumnDirective field="officeOwner" headerText={t('offices.OfficeOwner')} width="160" textAlign="Center" validationRules={{ required: true }}/>
              <ColumnDirective
                field="statusText"
                headerText={t('offices.Status')}
                width="120"
                textAlign="Center"
                defaultValue={getStatusText(statusOptions[0])}
                edit={statusEdit}
              />
              <ColumnDirective field="role" headerText={t('offices.Role')} width="120" textAlign="Center" editTemplate={RoleEditTemplate} />
              <ColumnDirective headerText={t('offices.Reinvite')} width='60' textAlign="Center" commands={commands}/>
            </ColumnsDirective>
            <Inject services={[Page, Toolbar, Sort, Filter, Edit, CommandColumn]} />
          </GridComponent>
        </div>
      </div>
    </div>
  );

}

export default Offices;